import type { FC } from "react";
import router from "./utils/router/router";
import { RouterProvider } from "react-router";
import "./utils/extensions";
import { QueryClient, QueryClientProvider, QueryCache, MutationCache } from "@tanstack/react-query";
import { ToastContainer } from "@toman/ui-kit-light";
import ErrorPage from "./pages/ErrorPage";
import PWAUpdateHandler from "./components/PWAUpdateHandler";
import KeycloakProvider from "./providers/keycloak/KeycloakProvider";
import ThemeProvider from "./providers/theme/ThemeProvider";
import * as Sentry from "@sentry/react";
import { initSentry, captureException, addBreadcrumb } from "./utils/sentry/sentryConfig";
import "./index.css";

const queryCache = new QueryCache({
  onError: (error: Error) => {
    addBreadcrumb("React Query error", "query", "error", {
      error: error instanceof Error ? error.message : String(error),
    });
    captureException(error, {
      tags: { error_type: "react_query_error" },
      level: "error",
    });
  },
});

const mutationCache = new MutationCache({
  onError: (error: Error) => {
    addBreadcrumb("React Query mutation error", "mutation", "error", {
      error: error instanceof Error ? error.message : String(error),
    });
    captureException(error, {
      tags: { error_type: "react_query_mutation_error" },
      level: "error",
    });
  },
});

const queryClient = new QueryClient({
  queryCache,
  mutationCache,
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      staleTime: Infinity,
      gcTime: 0,
    },
  },
});

initSentry(import.meta.env.VITE_APP_SENTRY_DSN);

const App: FC = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <KeycloakProvider>
          <PWAUpdateHandler>
            <div className="app">
              <ToastContainer />
              <Sentry.ErrorBoundary
                fallback={(errorData) => (
                  <ErrorPage
                    error={errorData.error instanceof Error ? errorData.error.message : undefined}
                  />
                )}
              >
                <RouterProvider router={router} />
              </Sentry.ErrorBoundary>
            </div>
          </PWAUpdateHandler>
        </KeycloakProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

export default App;
